
import { useEffect, useState } from "react";
import { measurementAPI, getCmId } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Modal, Spinner } from "../../components/ui";

const MEASUREMENT_LIMIT = 12;

const EMPTY_VALUES = () => Array(MEASUREMENT_LIMIT).fill("");

// Get configured fields from measurement API response
const getMeasurementLabels = (product) => {
  if (!product) return [];

  return Array.from({ length: MEASUREMENT_LIMIT }, (_, i) => {
    const label = product[`PM_M${i + 1}`] || "";

    return {
      index: i,
      label: String(label),
    };
  }).filter((field) => field.label.trim() !== "");
};

export default function MeasurementModal({
  open,
  onClose,
  product,
  ledgerId,
  onSaved,
}) {
  const toast = useToast();

  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  const [lmmId, setLmmId] = useState(0);

  const [savedValues, setSavedValues] = useState(EMPTY_VALUES());
  const [savedRemarks, setSavedRemarks] = useState("");

  const [values, setValues] = useState(EMPTY_VALUES());
  const [remarks, setRemarks] = useState("");

  const fields = getMeasurementLabels(product);

  const hasSaved = lmmId > 0;

  // --------------------------------------------------
  // INITIALIZE FORM FROM GET MEASUREMENT DETAILS
  // --------------------------------------------------

  useEffect(() => {
    if (!open || !product) return;

    setApiError("");

    const existingId = Number(product.LMM_ID) || 0;

    const loadedValues = Array.from(
      { length: MEASUREMENT_LIMIT },
      (_, i) => product[`LMM_M${i + 1}`] ?? ""
    );

    const loadedRemarks = product.LMM_Remarks ?? "";

    setLmmId(existingId);

    if (existingId > 0) {
      // Existing measurement
      setSavedValues(loadedValues);
      setSavedRemarks(loadedRemarks);

      setValues(loadedValues);
      setRemarks(loadedRemarks);
    } else {
      // New measurement
      setSavedValues(EMPTY_VALUES());
      setSavedRemarks("");

      setValues(EMPTY_VALUES());
      setRemarks("");
    }
  }, [open, product]);

  // --------------------------------------------------
  // UPDATE INPUT VALUES
  // --------------------------------------------------

  const updateValue = (index, value) => {
    setValues((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // --------------------------------------------------
  // CANCEL
  // --------------------------------------------------

  const handleCancel = () => {
    setValues([...savedValues]);
    setRemarks(savedRemarks);
    setApiError("");

    onClose();
  };

  // --------------------------------------------------
  // SAVE MEASUREMENT
  // --------------------------------------------------

  const handleSave = async () => {
    if (!product?.PM_ID || !ledgerId) {
      setApiError("Customer or product information is missing.");
      return;
    }

    if (fields.length === 0) {
      setApiError("No measurement fields are configured.");
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const cmId = getCmId();

      // Save request arguments
      const payload = {
        cmId: cmId,
        lmmId: lmmId,
        ledgerId: ledgerId,
        productId: product.PM_ID,
        values: values,
        remarks: remarks,
      };

      console.log("[Measurement] Save request:", {
        LMM_CM_ID: cmId,
        LMM_LM_ID: ledgerId,
        LMM_PM_ID: product.PM_ID,
        LMM_ID: lmmId,
        values,
        remarks,
      });

      const res = await measurementAPI.save(payload);

      console.log("[Measurement] Save response:", res);

      if (res?.status === "success") {
        const newLmmId =
          Number(res?.data?.LMM_ID) || lmmId || 0;

        setLmmId(newLmmId);

        setSavedValues([...values]);
        setSavedRemarks(remarks);

        toast(
          lmmId
            ? "Measurement updated!"
            : "Measurement saved!"
        );

        // Refresh second API through parent
        await onSaved?.();

        onClose();
      } else {
        setApiError(
          res?.message ||
          "Couldn't save measurement. Please try again."
        );
      }
    } catch (error) {
      console.error("[Measurement] Save error:", error);

      setApiError(
        "Network error. Please check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------
  // MODAL UI
  // --------------------------------------------------

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title={`📏 ${product?.PM_Name || "Measurement"}`}
      subtitle={
        hasSaved
          ? "Edit saved measurement"
          : "Add a new measurement"
      }
      footer={
        fields.length === 0 ? (
          <button
            className="btn btn-ghost"
            onClick={onClose}
          >
            Close
          </button>
        ) : (
          <>
            <button
              className="btn btn-ghost"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner /> Saving...
                </>
              ) : (
                "Save Measurement"
              )}
            </button>
          </>
        )
      }
    >

      {/* ERROR */}

      {apiError && (
        <div
          style={{
            background: "var(--red-dim)",
            border: "1px solid rgba(224,85,85,0.3)",
            borderRadius: "var(--radius-sm)",
            padding: "10px 12px",
            marginBottom: 14,
            fontSize: 13,
            color: "var(--red)",
            lineHeight: 1.5,
          }}
        >
          {apiError}
        </div>
      )}

      {fields.length === 0 ? (

        <div
          style={{
            color: "var(--text-muted)",
            fontSize: 13.5,
          }}
        >
          Measurement configuration is not available.
          Please close this window and reload the product details.
        </div>

      ) : hasSaved ? (

        /* EXISTING MEASUREMENT */

        <div style={{ overflowX: "auto" }}>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >

            <thead>
              <tr>
                <th style={thStyle}>Measurement</th>

                <th
                  style={{
                    ...thStyle,
                    textAlign: "center",
                  }}
                >
                  Previous
                </th>

                <th
                  style={{
                    ...thStyle,
                    textAlign: "right",
                  }}
                >
                  New
                </th>
              </tr>
            </thead>

            <tbody>

              {fields.map(({ index, label }) => (

                <tr key={index}>

                  <td style={tdStyle}>
                    {label}
                  </td>

                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "center",
                      color: "var(--text-muted)",
                    }}
                  >
                    {savedValues[index] || "—"}
                  </td>

                  <td
                    style={{
                      ...tdStyle,
                      textAlign: "right",
                    }}
                  >
                    <input
                      className="form-input"
                      style={{
                        width: 90,
                        textAlign: "right",
                      }}
                      value={values[index] ?? ""}
                      onChange={(e) =>
                        updateValue(index, e.target.value)
                      }
                      placeholder="e.g. 38"
                      inputMode="decimal"
                    />
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      ) : (

        /* NEW MEASUREMENT */

        <div className="form-grid">

          {fields.map(({ index, label }) => (

            <div
              className="form-group"
              key={index}
            >

              <label className="form-label">
                {label}
              </label>

              <input
                className="form-input"
                value={values[index] ?? ""}
                onChange={(e) =>
                  updateValue(index, e.target.value)
                }
                placeholder="e.g. 38"
                inputMode="decimal"
              />

            </div>

          ))}

        </div>

      )}

      {/* REMARKS */}

      {fields.length > 0 && (

        <div
          className="form-group"
          style={{ marginTop: 14 }}
        >

          <label className="form-label">
            Remarks
          </label>

          <textarea
            className="form-textarea"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Any notes for this measurement"
          />

        </div>

      )}

    </Modal>
  );
}

// --------------------------------------------------
// TABLE STYLES
// --------------------------------------------------

const thStyle = {
  textAlign: "left",
  padding: "8px 6px",
  borderBottom: "1px solid var(--border, #2a2a2a)",
  color: "var(--text-muted)",
  fontWeight: 500,
};

const tdStyle = {
  padding: "8px 6px",
  borderBottom: "1px solid var(--border, #1f1f1f)",
};
